// src/features/presence/trends-section.tsx
import { Kicker } from "@/components/iris/kicker";
import { CollapsibleCard } from "@/features/statistics/chart-primitives";
import { usePresenceTrends } from "@/hooks/use-presence";
import { formatCount } from "@/lib/format-count";
import type { Env } from "@/lib/types";
import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface TrendsSectionProps {
  env: Env;
}

const METRICS = [
  { key: "connected", label: "Connected" },
  { key: "active", label: "Active" },
  { key: "idle", label: "Idle" },
  { key: "concurrent", label: "Concurrent" },
] as const;

const RANGES = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
] as const;

const LINE_COLOR = "#22d3ff"; // --iris-neon-cyan // token-exempt
const GRID_STROKE = "rgba(255,255,255,0.04)";
const AXIS_TICK_FILL = "#6b7184"; // --iris-fg-4 // token-exempt

const TOOLTIP_STYLE = {
  background: "var(--iris-bg-3)",
  border: "1px solid var(--iris-line-2)",
  borderRadius: "var(--iris-r-sm)",
  fontSize: "var(--iris-t-small)",
  color: "var(--iris-fg-2)",
} as const;

/** Small segmented control matching the EnvPill chip style. */
function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className="iris-chip"
          data-active={value === o.key}
          style={{
            cursor: "pointer",
            border: "1px solid var(--iris-line-2)",
            borderRadius: "var(--iris-r-sm)",
            padding: "4px 10px",
            fontSize: 12,
            background: value === o.key ? "var(--iris-bg-3)" : "transparent",
            color: value === o.key ? "var(--iris-fg-1)" : "var(--iris-fg-4)",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * IR-1148 §4 — presence over time. Metric + range selectors drive a line chart
 * of the chosen presence series. Re-fetches via {@link usePresenceTrends} when
 * env/metric/range change.
 */
export function TrendsSection({ env }: TrendsSectionProps) {
  const [metric, setMetric] = useState<(typeof METRICS)[number]["key"]>("active");
  const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("7d");
  const { data, isLoading } = usePresenceTrends(env, metric, range);

  const series = (data?.data.series ?? []).map((p) => ({ t: p.t, value: p.value }));

  return (
    <>
      <Kicker style={{ marginTop: 32, marginBottom: 12 }}>Presence Over Time</Kicker>
      <CollapsibleCard title="Trends">
        <div
          style={{
            display: "flex",
            gap: 16,
            marginBottom: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Segmented options={METRICS} value={metric} onChange={setMetric} />
          <Segmented options={RANGES} value={range} onChange={setRange} />
        </div>
        <div style={{ height: 240 }}>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--iris-fg-5)",
                fontSize: "var(--iris-t-small)",
              }}
            >
              Loading…
            </div>
          ) : series.length === 0 ? (
            <div
              style={{
                display: "flex",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--iris-fg-5)",
                fontSize: "var(--iris-t-small)",
              }}
            >
              No presence data for this range
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis
                  dataKey="t"
                  tick={{ fontSize: 10, fill: AXIS_TICK_FILL }}
                  tickFormatter={(v: string) => {
                    const d = new Date(v);
                    return Number.isNaN(d.getTime())
                      ? String(v)
                      : `${String(d.getMonth() + 1)}/${String(d.getDate())}`;
                  }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: AXIS_TICK_FILL }}
                  tickFormatter={(v: number) => formatCount(v)}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(label) => {
                    const d = new Date(String(label));
                    return Number.isNaN(d.getTime()) ? String(label) : d.toLocaleString();
                  }}
                  formatter={(value) => [(Number(value) || 0).toLocaleString(), metric]}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  name={metric}
                  stroke={LINE_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </CollapsibleCard>
    </>
  );
}
